// Pure editing operations over the builder draft (§6 of the plan).
//
// The draft is the editable copy of the extraction result; the extraction
// snapshots stay immutable. Every operation here returns a NEW draft object
// (never mutates its input) so React state updates and the autosave layer
// can treat drafts as values. Every draft field carries provenance:
//   'ai'     — untouched extraction output
//   'edited' — the human changed something (revert-to-extracted available)
//   'added'  — created from scratch or promoted from a bucket (no snapshot)
//
// Milestone 2 scope: reorder is up/down buttons (Decision #17 — no drag),
// and there is no re-extract button yet, so nothing here merges snapshots.
//
// Pure module: no React, no Supabase, no fetch — node-testable.

import { normalizeFieldType } from './fmxCustomFieldsApi.js';
import { normalizeFieldset } from './fieldsets.js';
import { FLAGS_BY_TYPE } from './validateFieldDraft.js';

const OPTION_TYPES = ['dropDownList'];
const BEHAVIOR_FLAGS = Object.keys(FLAGS_BY_TYPE);

// Attributes restored by revert-to-extracted — the stage-2 field shape.
const SNAPSHOT_KEYS = [
  'name', 'fieldset', 'fieldType', 'isRequired', 'options', 'allowOtherOption',
  'allowMultipleSelections', 'allowMultipleLines', 'disallowNegativeValues',
  'defaults', 'description', 'confidence', 'rationale', 'sourcePage',
];

// ── Internals ───────────────────────────────────────────────────────────────

function cloneDraft(draft) {
  return {
    ...draft,
    fields: (draft?.fields || []).map(f => ({ ...f })),
    dismissed: [...(draft?.dismissed || [])],
    deleted: [...(draft?.deleted || [])],
    promoted: [...(draft?.promoted || [])],
  };
}

// Fields of one fieldset in display order (sortPosition, name tiebreaker —
// the same ordering buildAllPostBodies uses within a fieldset).
export function fieldsInFieldset(draft, fieldset) {
  const fs = normalizeFieldset(fieldset);
  return (draft?.fields || [])
    .filter(f => normalizeFieldset(f.fieldset) === fs)
    .sort((a, b) => {
      const pa = Number.isInteger(a.sortPosition) ? a.sortPosition : Number.MAX_SAFE_INTEGER;
      const pb = Number.isInteger(b.sortPosition) ? b.sortPosition : Number.MAX_SAFE_INTEGER;
      return (pa - pb) || String(a.name || '').localeCompare(String(b.name || ''));
    });
}

// Rewrite sortPosition 0..n-1 for a fieldset's fields, in their current
// display order, inside an already-cloned draft.
function renumberGroup(draft, fieldset) {
  fieldsInFieldset(draft, fieldset).forEach((f, i) => { f.sortPosition = i; });
}

function markEdited(field) {
  if (field.provenance === 'ai') field.provenance = 'edited';
}

// Monotonic field ids: draftFromStage2 emits fld-1..fld-n; `seq` keeps ids
// unique across add/delete cycles so a deleted field's id is never reused.
function nextFieldId(draft) {
  let max = draft.seq || 0;
  for (const f of draft.fields) {
    const m = /^fld-(\d+)$/.exec(f.id || '');
    if (m) max = Math.max(max, parseInt(m[1], 10));
  }
  return { id: `fld-${max + 1}`, seq: max + 1 };
}

// ── Field edits ─────────────────────────────────────────────────────────────

/**
 * Apply a plain attribute patch (name, description, isRequired, options,
 * defaults, behavior flags…) to one field. Marks 'ai' fields 'edited'.
 * Type and fieldset changes have their own operations below — they carry
 * side effects a bare patch must not.
 */
export function updateField(draft, fieldId, patch) {
  const next = cloneDraft(draft);
  const field = next.fields.find(f => f.id === fieldId);
  if (!field) return draft;
  Object.assign(field, patch);
  markEdited(field);
  return next;
}

/**
 * Change a field's type — the control swaps and type-gated state re-gates:
 * behavior flags illegal under the new type are dropped (a dropdown that
 * becomes a text field loses allowMultipleSelections, visibly), and options/
 * defaults are cleared when the new type doesn't take options (the options
 * editor disappears, so keeping them would strand an uneditable error).
 * Revert-to-extracted can always restore the original shape.
 */
export function changeFieldType(draft, fieldId, newType) {
  const fieldType = normalizeFieldType(newType);
  if (!fieldType) return draft;
  const next = cloneDraft(draft);
  const field = next.fields.find(f => f.id === fieldId);
  if (!field || field.fieldType === fieldType) return draft;
  field.fieldType = fieldType;
  for (const flag of BEHAVIOR_FLAGS) {
    if (field[flag] === true && !FLAGS_BY_TYPE[flag].includes(fieldType)) field[flag] = false;
  }
  if (!OPTION_TYPES.includes(fieldType)) {
    field.options = [];
    field.defaults = [];
  }
  markEdited(field);
  return next;
}

/**
 * Move a field to another (confirmed) fieldset group. It lands at the end of
 * the target group; both groups renumber. Validation re-runs against the new
 * fieldset's rules by construction (the validator reads field.fieldset).
 */
export function changeFieldset(draft, fieldId, newFieldset) {
  const fieldset = normalizeFieldset(newFieldset);
  if (!fieldset) return draft;
  const next = cloneDraft(draft);
  const field = next.fields.find(f => f.id === fieldId);
  if (!field || normalizeFieldset(field.fieldset) === fieldset) return draft;
  const oldFieldset = field.fieldset;
  field.fieldset = fieldset;
  field.sortPosition = Number.MAX_SAFE_INTEGER; // sinks to the target group's end
  renumberGroup(next, fieldset);
  renumberGroup(next, oldFieldset);
  markEdited(field);
  return next;
}

/**
 * Up/down reorder within the field's fieldset group (direction -1 | +1).
 * No-op at the boundaries. Rewrites sortPosition 0..n-1 for the group.
 */
export function moveField(draft, fieldId, direction) {
  const source = (draft?.fields || []).find(f => f.id === fieldId);
  if (!source) return draft;
  const group = fieldsInFieldset(draft, source.fieldset);
  const at = group.findIndex(f => f.id === fieldId);
  const to = at + (direction < 0 ? -1 : 1);
  if (at === -1 || to < 0 || to >= group.length) return draft;

  const next = cloneDraft(draft);
  const order = group.map(f => f.id);
  [order[at], order[to]] = [order[to], order[at]];
  order.forEach((id, i) => {
    next.fields.find(f => f.id === id).sortPosition = i;
  });
  // Reordering is layout, not content — provenance is deliberately untouched.
  return next;
}

/**
 * Move a field to an explicit position within its fieldset group (0-based;
 * out-of-range clamps to the ends) — the typed-number counterpart of the
 * up/down buttons. Every other field in the group renumbers around it.
 */
export function moveFieldToPosition(draft, fieldId, position) {
  const source = (draft?.fields || []).find(f => f.id === fieldId);
  if (!source || !Number.isInteger(position)) return draft;
  const group = fieldsInFieldset(draft, source.fieldset);
  const at = group.findIndex(f => f.id === fieldId);
  const to = Math.min(Math.max(position, 0), group.length - 1);
  if (at === -1 || at === to) return draft;

  const next = cloneDraft(draft);
  const order = group.map(f => f.id);
  order.splice(at, 1);
  order.splice(to, 0, fieldId);
  order.forEach((id, i) => {
    next.fields.find(f => f.id === id).sortPosition = i;
  });
  // Like moveField: layout, not content — provenance untouched.
  return next;
}

/**
 * Add a blank field to a fieldset group, marked 'added' (no snapshot link).
 * The caller opens it for editing; validation immediately flags the empty
 * name, which is the intended "finish me" signal.
 */
export function addField(draft, fieldset, presets = {}) {
  const fs = normalizeFieldset(fieldset);
  if (!fs) return draft;
  const next = cloneDraft(draft);
  const { id, seq } = nextFieldId(next);
  next.seq = seq;
  next.fields.push({
    id,
    provenance: 'added',
    sourceItemId: null,
    sortPosition: fieldsInFieldset(next, fs).length,
    name: '',
    fieldset: fs,
    fieldType: 'text',
    isRequired: false,
    options: [],
    allowOtherOption: false,
    allowMultipleSelections: false,
    allowMultipleLines: false,
    disallowNegativeValues: false,
    defaults: [],
    description: null,
    confidence: null,
    rationale: null,
    sourcePage: null,
    ...presets,
  });
  return next;
}

/**
 * Delete a field from the draft. Extraction-born fields ('ai'/'edited') are
 * recorded in draft.deleted so increment 2's re-extraction merge can honor
 * "deleted stays deleted"; 'added' fields simply vanish. Deleting a field
 * that came from a bucket promotion reopens the bucket entry so the item
 * isn't stranded as "promoted" with no field.
 */
export function deleteField(draft, fieldId) {
  const field = (draft?.fields || []).find(f => f.id === fieldId);
  if (!field) return draft;
  const next = cloneDraft(draft);
  next.fields = next.fields.filter(f => f.id !== fieldId);
  next.promoted = next.promoted.filter(p => p.fieldId !== fieldId);
  if (field.provenance !== 'added') {
    next.deleted.push({ sourceItemId: field.sourceItemId || null, name: field.name });
  }
  renumberGroup(next, field.fieldset);
  return next;
}

// The immutable snapshot's version of a field, or null (added fields and
// fields whose sourceItemId isn't in the snapshot have nothing to revert to).
export function extractedVersionOf(field, stage2) {
  if (!field?.sourceItemId) return null;
  return (stage2?.fields || []).find(f => f.sourceItemId === field.sourceItemId) || null;
}

/**
 * Per-field revert: copy the original extracted values back from the
 * immutable stage-2 snapshot. Provenance returns to 'ai'. The field keeps
 * its place in its current group unless the revert changes its fieldset,
 * in which case it moves to the end of the restored group.
 */
export function revertField(draft, fieldId, stage2) {
  const field = (draft?.fields || []).find(f => f.id === fieldId);
  const original = extractedVersionOf(field, stage2);
  if (!original) return draft;
  const oldFieldset = field.fieldset;
  let next = cloneDraft(draft);
  const target = next.fields.find(f => f.id === fieldId);
  for (const key of SNAPSHOT_KEYS) {
    const v = original[key];
    target[key] = Array.isArray(v) ? [...v] : v;
  }
  target.provenance = 'ai';
  if (normalizeFieldset(oldFieldset) !== normalizeFieldset(target.fieldset)) {
    target.sortPosition = Number.MAX_SAFE_INTEGER; // sinks to the group end
    renumberGroup(next, target.fieldset);
    renumberGroup(next, oldFieldset);
  }
  return next;
}

/**
 * Merge several same-named fields into one (rule R1/R2), keeping `keepId` as
 * the survivor. The survivor absorbs the others' source items and sections —
 * so its request-type scope, when derived from sections, widens to cover all
 * of them — and the rest are removed. Used by the builder's one-click merge
 * on a duplicate-name error, which is the remedy that error names.
 *
 * Option lists are NOT unioned: the automatic merge already ran at extraction
 * and deliberately refused this group (divergent options), so the human is
 * choosing the survivor's options by choosing the survivor.
 */
export function mergeFieldsInto(draft, keepId, mergeIds) {
  const next = cloneDraft(draft);
  const keep = next.fields.find(f => f.id === keepId);
  const absorbed = next.fields.filter(f => mergeIds.includes(f.id) && f.id !== keepId);
  if (!keep || absorbed.length === 0) return draft;

  const sourceIds = new Set(keep.mergedFrom?.length ? keep.mergedFrom : [keep.sourceItemId]);
  const sections = new Set(keep.mergedSections || []);
  for (const f of absorbed) {
    for (const id of f.mergedFrom?.length ? f.mergedFrom : [f.sourceItemId]) {
      if (id) sourceIds.add(id);
    }
    for (const s of f.mergedSections || []) sections.add(s);
    if (f.isRequired) keep.isRequired = true;
  }
  keep.mergedFrom = [...sourceIds].filter(Boolean);
  keep.mergedSections = [...sections];
  markEdited(keep);

  const removed = new Set(absorbed.map(f => f.id));
  next.fields = next.fields.filter(f => !removed.has(f.id));
  next.promoted = next.promoted.filter(p => !removed.has(p.fieldId));
  // A merge is a modelling decision, not a deletion — the questions are still
  // represented, by the survivor. Recording them as deleted would wrongly
  // suppress them from a future re-extraction (Decision #14).
  renumberGroup(next, keep.fieldset);
  return next;
}

/**
 * Undo a merge: the field keeps its primary source item, and the absorbed
 * ones are dropped from its provenance. (The absorbed fields are not
 * recreated — re-running extraction is how you get them back; this exists so
 * a mistaken merge doesn't leave a field claiming sections it shouldn't
 * be scoped to.)
 */
export function splitField(draft, fieldId) {
  const field = (draft?.fields || []).find(f => f.id === fieldId);
  if (!field || !(field.mergedFrom?.length > 1)) return draft;
  const next = cloneDraft(draft);
  const target = next.fields.find(f => f.id === fieldId);
  const primary = target.sourceItemId || target.mergedFrom[0];
  target.mergedFrom = [primary];
  target.mergedSections = (target.mergedSections || []).slice(0, 1);
  if (target.scopeOverride) {
    const { requestTypeIDs, ...rest } = target.scopeOverride;
    target.scopeOverride = Object.keys(rest).length ? rest : undefined;
  }
  markEdited(target);
  return next;
}

/** Set (or clear) a field's hand-edited visibility formula. */
export function setVisibilityFormula(draft, fieldId, formula) {
  const next = cloneDraft(draft);
  const field = next.fields.find(f => f.id === fieldId);
  if (!field) return draft;
  const text = (formula ?? '').trim();
  field.visibilityFormula = text || null;
  // Marked so re-deriving rules never clobbers a hand-written formula.
  field.visibilityFormulaEdited = true;
  markEdited(field);
  return next;
}

// ── Buckets: dismiss / restore / promote ────────────────────────────────────
//
// Bucket items live in draft.buckets (nativeMappings / prerequisites /
// unsupportedLogic / flags) and draft.quarantined — copied once from the
// snapshot and never edited. Dismiss and promote are recorded as overlays
// (draft.dismissed / draft.promoted) keyed by bucketItemKey, so the
// underlying lists stay untouched.

// Stable identity for a bucket item: sourceItemId where the extraction
// provided one, list position otherwise (the lists never reorder).
export function bucketItemKey(bucket, item, index) {
  return `${bucket}:${item?.sourceItemId || `i${index}`}`;
}

function itemsOf(draft, bucket) {
  if (bucket === 'quarantined') return draft?.quarantined || [];
  return draft?.buckets?.[bucket] || [];
}

/**
 * A bucket's items with their keys and overlay status:
 * [{ key, item, status: 'open' | 'dismissed' | 'promoted', reason?, promotedFieldId? }]
 */
export function bucketItems(draft, bucket) {
  const dismissedByKey = new Map((draft?.dismissed || []).map(d => [d.key, d]));
  const promotedByKey = new Map((draft?.promoted || []).map(p => [p.key, p]));
  return itemsOf(draft, bucket).map((item, index) => {
    const key = bucketItemKey(bucket, item, index);
    const dismissed = dismissedByKey.get(key);
    const promoted = promotedByKey.get(key);
    return {
      key,
      item,
      status: promoted ? 'promoted' : dismissed ? 'dismissed' : 'open',
      reason: dismissed?.reason ?? null,
      promotedFieldId: promoted?.fieldId ?? null,
    };
  });
}

/** Dismiss a bucket item; the reason is kept in the session record. */
export function dismissBucketItem(draft, bucket, key, reason) {
  const next = cloneDraft(draft);
  next.dismissed = next.dismissed.filter(d => d.key !== key);
  next.dismissed.push({ bucket, key, reason: (reason || '').trim() || null });
  return next;
}

/** Undo a dismissal — the item returns to its bucket's open list. */
export function restoreBucketItem(draft, bucket, key) {
  const next = cloneDraft(draft);
  next.dismissed = next.dismissed.filter(d => d.key !== key);
  return next;
}

// What a promoted bucket item should be named, per bucket shape (§4).
export function bucketItemName(bucket, item) {
  if (!item || typeof item !== 'object') return '';
  const name = item.label || item.value || item.issue || item.text
    || item.raw?.name || item.raw?.label || '';
  return String(name).slice(0, 200);
}

/**
 * Promote: "create as a custom field anyway" — the item enters the form as
 * a draft field (provenance 'added'; sourceItemId kept when the extraction
 * provided one, so jump-to-source still works). The bucket shows it as
 * promoted rather than open; deleting the created field reopens it.
 */
export function promoteBucketItem(draft, bucket, key, item, fieldset) {
  const fs = normalizeFieldset(fieldset);
  if (!fs) return draft;
  let next = addField(draft, fs, {
    name: bucketItemName(bucket, item),
    sourceItemId: item?.sourceItemId || null,
    sourcePage: Number.isInteger(item?.sourcePage) ? item.sourcePage : null,
  });
  if (next === draft) return draft;
  const fieldId = next.fields[next.fields.length - 1].id;
  next.dismissed = next.dismissed.filter(d => d.key !== key);
  next.promoted = next.promoted.filter(p => p.key !== key);
  next.promoted.push({ bucket, key, fieldId });
  return next;
}
